// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * StatusColorUtils
 * 
 * Centralized utility for consistent color-coded status indicators across the app
 * Provides standardized color thresholds and styling for health, fuel, power, etc.
 */

/**
 * Standard color thresholds
 */
export const THRESHOLDS = {
  OPTIMAL: 80,    // >= 80% = Green
  WARNING: 60,    // 60-79% = Orange
  CRITICAL: 30,   // 30-59% = Red
  DESTROYED: 0    // < 30% = Dark Red
};

/**
 * Standard status colors (cyberpunk theme)
 */
export const COLORS = {
  OPTIMAL: '#00ff88',      // Green - good status
  WARNING: '#ffaa00',      // Orange - caution
  CRITICAL: '#ff4444',     // Red - danger
  DESTROYED: '#aa0000',    // Dark Red - destroyed
  INFO: '#00ffff',         // Cyan - information
  ACCENT: '#ff00ff'        // Magenta - accent
};

/**
 * Get color based on percentage value
 * @param {number} value - Current value
 * @param {number} max - Maximum value (default 100)
 * @returns {string} Hex color code
 */
export const getColorForValue = (value, max = 100) => {
  const percentage = (value / max) * 100;
  
  if (percentage >= THRESHOLDS.OPTIMAL) return COLORS.OPTIMAL;
  if (percentage >= THRESHOLDS.WARNING) return COLORS.WARNING;
  if (percentage >= THRESHOLDS.CRITICAL) return COLORS.CRITICAL;
  return COLORS.DESTROYED;
};

/**
 * Get status level based on percentage
 * @param {number} value - Current value
 * @param {number} max - Maximum value (default 100)
 * @returns {string} Status level: 'optimal', 'warning', 'critical', 'destroyed'
 */
export const getStatusLevel = (value, max = 100) => {
  const percentage = (value / max) * 100;
  
  if (percentage >= THRESHOLDS.OPTIMAL) return 'optimal';
  if (percentage >= THRESHOLDS.WARNING) return 'warning';
  if (percentage >= THRESHOLDS.CRITICAL) return 'critical';
  return 'destroyed';
};

/**
 * Get status text based on percentage
 * @param {number} value - Current value
 * @param {number} max - Maximum value (default 100)
 * @returns {string} Status text
 */
export const getStatusText = (value, max = 100) => {
  const percentage = (value / max) * 100;
  
  if (percentage >= THRESHOLDS.OPTIMAL) return 'OPTIMAL';
  if (percentage >= THRESHOLDS.WARNING) return 'DAMAGED';
  if (percentage >= THRESHOLDS.CRITICAL) return 'CRITICAL';
  return 'DESTROYED';
};

/**
 * Get status icon based on percentage
 * @param {number} value - Current value
 * @param {number} max - Maximum value (default 100)
 * @returns {string} Status emoji icon
 */
export const getStatusIcon = (value, max = 100) => {
  const percentage = (value / max) * 100;
  
  if (percentage >= THRESHOLDS.OPTIMAL) return '✓';
  if (percentage >= THRESHOLDS.WARNING) return '⚠️';
  if (percentage >= THRESHOLDS.CRITICAL) return '💥';
  return '☠️';
};

/**
 * Get glow effect CSS for status
 * @param {number} value - Current value
 * @param {number} max - Maximum value (default 100)
 * @returns {object} CSS style object with box-shadow and text-shadow
 */
export const getStatusGlow = (value, max = 100) => {
  const color = getColorForValue(value, max);
  return {
    boxShadow: `0 0 10px ${color}, 0 0 20px ${color}40`,
    textShadow: `0 0 10px ${color}`
  };
};

/**
 * Get pulsing animation class for critical status
 * @param {number} value - Current value
 * @param {number} max - Maximum value (default 100)
 * @returns {string} CSS class name or empty string
 */
export const getPulseClass = (value, max = 100) => {
  const percentage = (value / max) * 100;
  return percentage < THRESHOLDS.CRITICAL ? 'pulse-animation' : '';
};

/**
 * Format percentage with color
 * @param {number} value - Current value
 * @param {number} max - Maximum value (default 100)
 * @param {boolean} showIcon - Whether to show status icon (default false)
 * @returns {object} Object with color, text, icon
 */
export const formatStatusPercentage = (value, max = 100, showIcon = false) => {
  const percentage = (value / max) * 100;
  return {
    color: getColorForValue(value, max),
    text: `${percentage.toFixed(1)}%`,
    icon: showIcon ? getStatusIcon(value, max) : '',
    level: getStatusLevel(value, max)
  };
};

/**
 * Get background color with opacity for status badges
 * @param {number} value - Current value
 * @param {number} max - Maximum value (default 100)
 * @param {number} opacity - Opacity value 0-1 (default 0.2)
 * @returns {string} RGBA color string
 */
export const getStatusBackground = (value, max = 100, opacity = 0.2) => {
  const color = getColorForValue(value, max);
  // Convert hex to RGB
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Get border color for status indicators
 * @param {number} value - Current value
 * @param {number} max - Maximum value (default 100)
 * @param {number} opacity - Opacity value 0-1 (default 0.4)
 * @returns {string} RGBA color string
 */
export const getStatusBorder = (value, max = 100, opacity = 0.4) => {
  const color = getColorForValue(value, max);
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const StatusColorUtils = {
  THRESHOLDS,
  COLORS,
  getColorForValue,
  getStatusLevel,
  getStatusText,
  getStatusIcon,
  getStatusGlow,
  getPulseClass,
  formatStatusPercentage,
  getStatusBackground,
  getStatusBorder
};

export default StatusColorUtils;